import {
  AlertItem,
  MENU_ACTIONS,
  PRIORITY_BY_KEY,
} from '@/mockdata/notification/notifications'
import { FiberManualRecord } from '@mui/icons-material'
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import TagChips from './TagChips'
import Group from '@assets/Notification/Group.svg'
import { useState } from 'react'
import More from '@assets/Notification/More.svg'
import { useNavigate, useParams } from 'react-router-dom'

import { useAppTheme } from '@/theme/ThemeContext'
import { candidateCountView } from '@/pages/notification/candidateCount'

// ... existing imports

export default function AlertMessageCard({
  item,
  selectedTags = [],
  onTagClick,
  onKpiClick,
  enabled = true,
  onToggleEnabled,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  item: AlertItem
  selectedTags?: string[]
  onTagClick?: (tag: string) => void
  onKpiClick?: (item: AlertItem) => void
  enabled?: boolean
  onToggleEnabled?: (enabled: boolean) => void
  onEdit?: (id: number) => void
  onDuplicate?: (id: number) => void
  onDelete?: (id: number) => void
}) {
  const menuOverrides: Record<string, ((id: string | number) => void) | undefined> = {
    edit: onEdit as ((id: string | number) => void) | undefined,
    duplicate: onDuplicate as ((id: string | number) => void) | undefined,
    delete: onDelete as ((id: string | number) => void) | undefined,
  }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }
  const navigate = useNavigate()
  const { siteId } = useParams()

  const handleClose = () => setAnchorEl(null)

  const {
    title,
    severity,
    kpi,
    kpiComputedAt,
    text,
    createdAt,
    changedAt,
    createdBy,
    priority,
  } = item

  const count = candidateCountView(kpi, kpiComputedAt, enabled)
  const pr = PRIORITY_BY_KEY[priority]
  const dotColor = pr?.color ?? '#C0C2D6'
  const showDot = pr?.showDot ?? true

  const [tagsLocal, setTagsLocal] = useState<string[]>(item.tags)
  const [newlyAddedTags, setNewlyAddedTags] = useState<string[]>([])

  const handleAddTag = () => {
    const base = 'תגית'
    let i = 1
    let candidate = `${base} ${tagsLocal.length + 1}`
    while (tagsLocal.includes(candidate)) {
      candidate = `${base} ${++i}`
    }
    setTagsLocal(prev => [...prev, candidate])
    setNewlyAddedTags(prev => [...prev, candidate])
  }

  const handleRemoveTag = (tag: string) => {
    setTagsLocal(prev => prev.filter(t => t !== tag))
    setNewlyAddedTags(prev => prev.filter(t => t !== tag))
  }

  const cardBg = isDarkMode ? '#202B40' : '#FFFFFF'
  const textColor = isDarkMode ? '#FFFFFF' : '#003F61'
  const subTextColor = isDarkMode ? '#B0B0B0' : '#C0C2D6'
  const kpiBorder = isDarkMode ? '#374151' : '#EBEBF1'

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1, bgcolor: cardBg, borderColor: isDarkMode ? '#374151' : undefined }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" mb={1} gap={1}>
          <Typography variant="h6" fontWeight={700} color={textColor} sx={{fontFamily: 'Rubik, sans-serif',}}>
            {title}
          </Typography>
          {showDot && (
            <FiberManualRecord sx={{ fontSize: 10, color: dotColor }} />
          )}
          <Typography variant="body2" color={subTextColor} sx={{ fontSize: '12px',fontFamily: 'Rubik, sans-serif',}}>
            חומרה {severity}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch 
           checked={enabled}
           onChange={(e) => onToggleEnabled?.(e.target.checked)}
            />
          <IconButton size="small" onClick={handleOpenMenu}>
            <img 
              src={More} 
              alt="more" 
              style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                bgcolor: isDarkMode ? '#202B40' : '#EFF2F7',
                color: textColor,
                borderRadius: 2,
                boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
                minWidth: 220,
                mt: '10px',
                border: isDarkMode ? '1px solid #374151' : undefined,
              },
            }}
          >
            {MENU_ACTIONS.map(a => (
              <MenuItem
                key={a.key}
                onClick={() => {
                  const override = menuOverrides[a.key]
                  if (override) override(item.id)
                  else a.onClick(item.id)
                  handleClose()
                }}
                sx={{
                  py: 1.25,
                  fontSize: 14,
                  color: a.danger ? '#C62828' : textColor,
                  '&:hover': { bgcolor: isDarkMode ? '#2d4a7c' : '#E6EBF3' },
                  justifyContent: 'center',
                }}
              >
                {a.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>

      <Divider sx={{ my: 1, borderColor: isDarkMode ? '#374151' : undefined }} />
      <Typography variant="caption" color={subTextColor} mb={0.5} sx={{fontFamily: 'Rubik, sans-serif',}}>
        {`ת. יצירה: ${createdAt} | ת. שינוי: ${changedAt} | נוצר ע"י ${createdBy}`}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        mb={2}
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography
          variant="body2"
          color={textColor}
          sx={{
            width: '60%',
            fontFamily: 'Rubik, sans-serif',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {text}
        </Typography>

        <Box
          onClick={() => onKpiClick?.(item)}
          role="button"
          tabIndex={0}
          sx={{
            textAlign: 'center',
            minWidth: 78,
            border: `1px solid ${kpiBorder}`,
            borderRadius: '5px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Typography
            fontWeight={800}
            color={textColor}
            fontSize={count.pending ? '13px' : '20px'}
            lineHeight={count.pending ? '28px' : undefined}
            sx={{fontFamily: 'Rubik, sans-serif',}}
          >
            {count.value}
          </Typography>
          <Typography
            sx={{
              overflow: 'hidden',
              color: subTextColor,
              textOverflow: 'ellipsis',
              fontFamily: 'Rubik, sans-serif',
              fontSize: '10px',
              fontStyle: 'normal',
              fontWeight: 300,
              lineHeight: '120%',
            }}
          >
            יח׳ קצה
          </Typography>
          {count.caption && (
            <Typography
              sx={{
                color: subTextColor,
                fontFamily: 'Rubik, sans-serif',
                fontSize: '10px',
                fontWeight: 300,
                lineHeight: '120%',
                px: 0.5,
                pb: 0.25,
              }}
            >
              {count.caption}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <TagChips
          tags={tagsLocal}
          onTagClick={onTagClick}
          onRemoveTag={handleRemoveTag}
          selectedTags={selectedTags}
          onAddTag={handleAddTag}
          disabledTags={newlyAddedTags}
        />
        <Typography
          onClick={() => {
            navigate(
              `/${siteId}/notifications/manage-notifications/${item.id}/flow-chart`
            )
          }}
          bgcolor={isDarkMode ? '#2d4a7c' : "#F8FAFD"}
          px="4px"
          sx={{
            fontFamily: 'Rubik, sans-serif',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          <img 
            src={Group} 
            alt="Group" 
            style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
          />
        </Typography>
      </Stack>
    </Paper>
  )
}
